// Mirar un encargo: lo que trae el canal en directo, y lo que se pregunta
// cuando el canal no esta.
//
// **Las dos vias, no una.** El canal es lo que evita recargar; preguntar es lo
// que hace que perder el canal no cueste nada. Con solo la primera, una pestaña
// que se despierta de dormir se queda con lo ultimo que vio.

import { useEffect, useState } from 'react'

import { log } from '@/lib/logger'
import { getRun, RUN_IS_OVER, type Run } from '@/services/brainApi'
import { watchRun } from '@/services/runEvents'

/**
 * `vuelta` vuelve a abrir el seguimiento del mismo encargo. Hace falta al
 * contestarle: el encargo es el mismo, pero el canal pudo cortarse mientras
 * esperaba -un proxy, una pestaña dormida- y sin volver a preguntar la
 * pantalla no se enteraria de que siguio.
 */
export function useRun(runId: string | null, vuelta = 0) {
  const [run, setRun] = useState<Run | null>(null)

  useEffect(() => {
    if (!runId) {
      setRun(null)
      return
    }

    let alive = true
    // Lo primero es preguntar: asi la pantalla tiene algo que enseñar aunque el
    // canal tarde, y algo que enseñar si el canal no se puede abrir.
    //
    // Si falla -la red, un encargo que ya no existe-, se deja escrito y se
    // sigue: el canal puede traerlo igual, y una promesa rechazada sin nadie
    // que la recoja no avisa a nadie de nada.
    getRun(runId)
      .then((fetched) => {
        if (alive) setRun(fetched)
      })
      .catch((error) => log.warn('No se pudo leer el encargo', error))

    const stopWatching = watchRun(runId, (partial) => {
      if (!alive) return
      setRun((before) => (before ? { ...before, ...partial } : before))
      if (RUN_IS_OVER.has(partial.status)) {
        // Terminado: se vuelve a preguntar una vez, porque el aviso trae estado
        // y pasos, y lo demas -lo gastado, la hora de fin- vive en el encargo.
        getRun(runId)
          .then((fetched) => {
            if (alive) setRun(fetched)
          })
          .catch((error) => log.warn('No se pudo leer el encargo', error))
      }
    })

    return () => {
      alive = false
      stopWatching()
    }
  }, [runId, vuelta])

  return run
}
